import { getAuthHeader, API_BASE_URL } from './base';

export const getBrandkits = async () => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch brandkits");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching brandkits:", error);
    throw error;
  }
};

export const getBrandkit = async (brandkitId) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch brandkit");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching brandkit:", error);
    throw error;
  }
};

export const extractBrandFromUrl = async (url) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/extract-from-url`, {
      method: "POST",
      headers,
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to extract brand data");
    }

    return await response.json();
  } catch (error) {
    console.error("Error extracting brand from URL:", error);
    throw error;
  }
};

export const createBrandkit = async (name, method, extractedData = null) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit`, {
      method: "POST",
      headers,
      body: JSON.stringify({ name, method, extractedData }),
    });

    if (!response.ok) {
      throw new Error("Failed to create brandkit");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating brandkit:", error);
    throw error;
  }
};

export const uploadLogoToBrandkit = async (brandkitId, file) => {
  try {
    const headers = await getAuthHeader();
    const formData = new FormData();
    formData.append("logo", file);

    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/upload-logo`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to upload logo");
    }

    return await response.json();
  } catch (error) {
    console.error("Error uploading logo:", error);
    throw error;
  }
};

export const addLogoFromUrl = async (brandkitId, url, thumbnailUrl, name) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-logo-from-url`, {
      method: "POST",
      headers,
      body: JSON.stringify({ url, thumbnailUrl, name }),
    });

    if (!response.ok) throw new Error("Failed to add logo from URL");
    return await response.json();
  } catch (error) {
    console.error("Error adding logo from URL:", error);
    throw error;
  }
};

export const addMediaFromUrl = async (brandkitId, groupId, url, thumbnailUrl, type) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-media-from-url/${groupId}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ url, thumbnailUrl, type }),
    });

    if (!response.ok) throw new Error("Failed to add media from URL");
    return await response.json();
  } catch (error) {
    console.error("Error adding media from URL:", error);
    throw error;
  }
};

export const deleteLogoFromBrandkit = async (brandkitId, logoId) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/logo/${logoId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to delete logo");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting logo:", error);
    throw error;
  }
};

export const addFontToBrandkit = async (brandkitId, fontData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-font`, {
      method: "POST",
      headers,
      body: JSON.stringify(fontData),
    });

    if (!response.ok) {
      throw new Error("Failed to add font");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding font:", error);
    throw error;
  }
};

export const deleteFontFromBrandkit = async (brandkitId, fontId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/font/${fontId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to delete font");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting font:", error);
    throw error;
  }
};

export const addColorPaletteToBrandkit = async (brandkitId, paletteData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-color-palette`, {
      method: "POST",
      headers,
      body: JSON.stringify(paletteData),
    });

    if (!response.ok) {
      throw new Error("Failed to add color palette");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding color palette:", error);
    throw error;
  }
};

export const addColorToPalette = async (brandkitId, paletteId, hex) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-color-to-palette/${paletteId}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ hex }),
    });

    if (!response.ok) {
      throw new Error("Failed to add color to palette");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding color to palette:", error);
    throw error;
  }
};

export const updatePaletteColors = async (brandkitId, paletteId, colors) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/update-palette-colors/${paletteId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ colors }),
    });

    if (!response.ok) {
      throw new Error("Failed to update palette colors");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating palette colors:", error);
    throw error;
  }
};

export const addFontGroupToBrandkit = async (brandkitId, groupData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-font-group`, {
      method: "POST",
      headers,
      body: JSON.stringify(groupData),
    });

    if (!response.ok) {
      throw new Error("Failed to add font group");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding font group:", error);
    throw error;
  }
};

export const addFontToGroup = async (brandkitId, groupId, fontData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-font-to-group/${groupId}`, {
      method: "POST",
      headers,
      body: JSON.stringify(fontData),
    });

    if (!response.ok) {
      throw new Error("Failed to add font to group");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding font to group:", error);
    throw error;
  }
};

export const uploadFontToGroup = async (brandkitId, groupId, file, fontName) => {
  try {
    const headers = await getAuthHeader();
    const formData = new FormData();
    formData.append("font", file, file.name || "font.ttf");
    formData.append("name", fontName);

    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/upload-font-to-group/${groupId}`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to upload font");
    }

    return await response.json();
  } catch (error) {
    console.error("Error uploading font:", error);
    throw error;
  }
};

export const updateFontGroupFonts = async (brandkitId, groupId, fonts) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/update-font-group-fonts/${groupId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ fonts }),
    });

    if (!response.ok) {
      throw new Error("Failed to update font group fonts");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating font group fonts:", error);
    throw error;
  }
};

export const deleteFontGroupFromBrandkit = async (brandkitId, groupId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/font-group/${groupId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to delete font group");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting font group:", error);
    throw error;
  }
};

export const deleteColorPaletteFromBrandkit = async (brandkitId, paletteId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/delete-color-palette/${paletteId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to delete color palette");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting color palette:", error);
    throw error;
  }
};

export const addMediaGroupToBrandkit = async (brandkitId, name) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-media-group`, {
      method: "POST",
      headers,
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      throw new Error("Failed to add media group");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding media group:", error);
    throw error;
  }
};

export const addMediaToGroup = async (brandkitId, groupId, file) => {
  try {
    const headers = await getAuthHeader();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/add-media-to-group/${groupId}`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to add media to group");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding media to group:", error);
    throw error;
  }
};

export const updateMediaGroupItems = async (brandkitId, groupId, items) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/update-media-group-items/${groupId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      throw new Error("Failed to update media group items");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating media group items:", error);
    throw error;
  }
};

export const deleteMediaGroupFromBrandkit = async (brandkitId, groupId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/delete-media-group/${groupId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to delete media group");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting media group:", error);
    throw error;
  }
};

export const updateBrandkitBrandInfo = async (brandkitId, brandName, websiteUrl, about = '', toneOfVoice = '') => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/brand-info`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ brandName, websiteUrl, about, toneOfVoice }),
    });

    if (!response.ok) {
      throw new Error("Failed to update brand info");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating brand info:", error);
    throw error;
  }
};

export const deleteBrandkit = async (brandkitId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to delete brandkit");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting brandkit:", error);
    throw error;
  }
};

export const renameBrandkit = async (brandkitId, name) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/rename`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      throw new Error("Failed to rename brandkit");
    }

    return await response.json();
  } catch (error) {
    console.error("Error renaming brandkit:", error);
    throw error;
  }
};

export const updateBrandkit = async (brandkitId, updates) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error("Failed to update brandkit");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating brandkit:", error);
    throw error;
  }
};

export const generateThemeFromBrandkit = async (brandkit) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/ai/generate-theme`, {
      method: "POST",
      headers,
      body: JSON.stringify({ brandkit }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to generate theme");
    }

    return await response.json();
  } catch (error) {
    console.error("Error generating theme from brandkit:", error);
    throw error;
  }
};

// Kicks off server-side generation of brand themes. Uses keepalive so the
// request survives the redirect to the designer that follows immediately —
// the server keeps processing even after the page unloads.
export const generateBrandkitThemes = async (brandkitId) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/generate-themes`, {
      method: "POST",
      headers,
      keepalive: true,
    });
  } catch (error) {
    console.error("Error requesting brandkit theme generation:", error);
  }
};

export const getThemes = async (brandkitId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/themes`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch themes");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching themes:", error);
    throw error;
  }
};

export const createTheme = async (brandkitId, themeData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/themes`, {
      method: "POST",
      headers,
      body: JSON.stringify(themeData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create theme");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating theme:", error);
    throw error;
  }
};

export const updateTheme = async (brandkitId, themeId, themeData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/themes/${themeId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(themeData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update theme");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating theme:", error);
    throw error;
  }
};

export const deleteTheme = async (brandkitId, themeId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/themes/${themeId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete theme");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting theme:", error);
    throw error;
  }
};

export const getTheme = async (brandkitId, themeId) => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/brandkit/${brandkitId}/themes/${themeId}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch theme");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching theme:", error);
    throw error;
  }
};
